import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import {
  Edit,
  CreditCard,
  UserCog,
  Key,
  RefreshCw,
  Mail,
  Fingerprint,
  Power,
  Trash,
  TestTube,
} from "lucide-react";

interface AuthUserInfo {
  email_confirmed_at?: string;
  last_sign_in_at?: string;
}

interface UserActionsProps {
  user: {
    id: number;
    name: string;
    email: string;
    isActive: boolean;
  };
  authUserInfo?: AuthUserInfo | null;
  currentSubscription?: {
    id: number;
    status: string;
    subscriptionPlan?: {
      name: string;
      description?: string;
    };
  } | null;
  hasAdminPrivileges: boolean;
  canManageUserRoles: boolean;
  isBetaTester?: boolean;
  isDeleted?: boolean; // New prop to indicate if user is deleted
  loadingStates: {
    isResendingConfirmation: boolean;
    isResendingInvitation: boolean;
    isBanning: boolean;
    isUnbanning: boolean;
    isConfirmingEmail: boolean;
    isMakingBetaTester: boolean;
  };
  onEditProfile: () => void;
  onChangeSubscription: () => void;
  onManageRoles: () => void;
  onResetPassword: () => void;
  onResendOTP: () => void;
  onResendInvitation: () => void;
  onConfirmEmail: () => void;
  onBanAccount: () => void;
  onUnbanAccount: () => void;
  onDeleteAccount: () => void;
  onMakeBetaTester: () => void;
  onPermanentlyDelete?: () => void;
}

const UserActions: React.FC<UserActionsProps> = ({
  user,
  authUserInfo,
  currentSubscription,
  hasAdminPrivileges,
  canManageUserRoles,
  isBetaTester = false,
  isDeleted = false,
  loadingStates,
  onEditProfile,
  onChangeSubscription,
  onManageRoles,
  onResetPassword,
  onResendOTP,
  onResendInvitation,
  onConfirmEmail,
  onBanAccount,
  onUnbanAccount,
  onDeleteAccount,
  onMakeBetaTester,
  onPermanentlyDelete,
}) => {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Actions</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {/* Only show Edit Profile if user has admin privileges and can manage this user */}
        {hasAdminPrivileges && canManageUserRoles && (
          <Button
            className="w-full"
            variant="outline"
            onClick={onEditProfile}
            disabled={isDeleted}
            title={
              isDeleted
                ? "Cannot edit deleted user profile"
                : "Edit user profile"
            }
          >
            <Edit className="mr-2 h-4 w-4" />
            Edit Profile
          </Button>
        )}

        {/* Only show Change Subscription if user has admin privileges and can manage this user */}
        {hasAdminPrivileges && canManageUserRoles && (
          <Button
            className="w-full"
            variant="outline"
            onClick={onChangeSubscription}
            disabled={isDeleted}
            title={
              isDeleted
                ? "Cannot manage subscription for deleted user"
                : currentSubscription
                ? "Manage user subscription"
                : "Assign subscription to user"
            }
          >
            <CreditCard className="mr-2 h-4 w-4" />
            {currentSubscription
              ? "Manage Subscription"
              : "Assign Subscription"}
          </Button>
        )}

        {/* Only show Manage Roles if user has admin privileges and can manage this user */}
        {hasAdminPrivileges && canManageUserRoles && (
          <Button
            className="w-full"
            variant="outline"
            onClick={onManageRoles}
            disabled={isDeleted}
            title={
              isDeleted
                ? "Cannot manage roles for deleted user"
                : "Manage user roles"
            }
          >
            <UserCog className="mr-2 h-4 w-4" />
            Manage Roles
          </Button>
        )}

        {/* Only show Make Beta Tester if user has admin privileges and can manage this user */}
        {hasAdminPrivileges && canManageUserRoles && (
          <Button
            className="w-full"
            variant="outline"
            onClick={onMakeBetaTester}
            disabled={
              loadingStates.isMakingBetaTester || isBetaTester || isDeleted
            }
            title={
              isDeleted
                ? "Cannot make deleted user a beta tester"
                : isBetaTester
                ? "User is already a beta tester"
                : "Make this user a beta tester"
            }
          >
            {loadingStates.isMakingBetaTester ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Adding...
              </>
            ) : (
              <>
                <TestTube className="mr-2 h-4 w-4" />
                {isBetaTester ? "Already Beta Tester" : "Make Beta Tester"}
              </>
            )}
          </Button>
        )}

        {/* Only show Reset Password if user has admin privileges and can manage this user */}
        {hasAdminPrivileges && canManageUserRoles && (
          <Button
            className="w-full"
            variant="outline"
            onClick={onResetPassword}
            disabled={isDeleted}
            title={
              isDeleted
                ? "Cannot reset password for deleted user"
                : "Reset user password"
            }
          >
            <Key className="mr-2 h-4 w-4" />
            Reset Password
          </Button>
        )}

        {/* Show Resend OTP button */}
        <Button
          className="w-full"
          variant="outline"
          onClick={onResendOTP}
          disabled={
            isDeleted ||
            loadingStates.isResendingConfirmation ||
            !!authUserInfo?.email_confirmed_at ||
            !!authUserInfo?.last_sign_in_at ||
            !canManageUserRoles
          }
          title={
            isDeleted
              ? "Cannot resend OTP for deleted user"
              : authUserInfo?.email_confirmed_at
              ? "Email already confirmed"
              : authUserInfo?.last_sign_in_at
              ? "User has already logged in"
              : !canManageUserRoles
              ? "You cannot manage users with equal or higher role priority than yours"
              : "Resend verification code"
          }
        >
          {loadingStates.isResendingConfirmation ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Sending...
            </>
          ) : (
            <>
              <RefreshCw className="mr-2 h-4 w-4" />
              Resend OTP
            </>
          )}
        </Button>

        {/* Show Resend Invitation button */}
        <Button
          className="w-full"
          variant="outline"
          onClick={onResendInvitation}
          disabled={
            isDeleted ||
            loadingStates.isResendingInvitation ||
            !!authUserInfo?.email_confirmed_at ||
            !!authUserInfo?.last_sign_in_at ||
            !canManageUserRoles
          }
          title={
            isDeleted
              ? "Cannot resend invitation for deleted user"
              : authUserInfo?.email_confirmed_at
              ? "Email already confirmed"
              : authUserInfo?.last_sign_in_at
              ? "User has already logged in"
              : !canManageUserRoles
              ? "You cannot manage users with equal or higher role priority than yours"
              : "Resend invitation email"
          }
        >
          {loadingStates.isResendingInvitation ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Sending...
            </>
          ) : (
            <>
              <Mail className="mr-2 h-4 w-4" />
              Resend Invitation
            </>
          )}
        </Button>

        {/* Show Manually Confirm Email button if admin */}
        {hasAdminPrivileges && (
          <Button
            className="w-full"
            variant="outline"
            onClick={onConfirmEmail}
            disabled={
              isDeleted ||
              loadingStates.isConfirmingEmail ||
              !!authUserInfo?.email_confirmed_at ||
              !canManageUserRoles
            }
            title={
              isDeleted
                ? "Cannot confirm email for deleted user"
                : authUserInfo?.email_confirmed_at
                ? "Email already confirmed"
                : !canManageUserRoles
                ? "You cannot manage users with equal or higher role priority than yours"
                : "Manually verify email"
            }
          >
            {loadingStates.isConfirmingEmail ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Confirming...
              </>
            ) : (
              <>
                <Fingerprint className="mr-2 h-4 w-4" />
                Manually Confirm Email
              </>
            )}
          </Button>
        )}

        {/* Add a divider before destructive actions */}
        {hasAdminPrivileges && canManageUserRoles && (
          <div className="relative my-4">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-muted-foreground/20" />
            </div>
            <div className="relative flex justify-center text-xs">
              <span className="bg-card px-2 text-muted-foreground">
                Danger Zone
              </span>
            </div>
          </div>
        )}

        {/* Only show Ban/Unban Account if user has admin privileges and can manage this user */}
        {hasAdminPrivileges && canManageUserRoles && (
          <Button
            className="w-full text-destructive hover:text-destructive hover:bg-destructive/10"
            variant="outline"
            onClick={user?.isActive ? onBanAccount : onUnbanAccount}
            disabled={
              isDeleted ||
              (user?.isActive
                ? loadingStates.isBanning
                : loadingStates.isUnbanning)
            }
            title={
              isDeleted
                ? "Cannot ban/unban deleted user"
                : user?.isActive
                ? "Ban user account"
                : "Unban user account"
            }
          >
            {user?.isActive ? (
              loadingStates.isBanning ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Banning...
                </>
              ) : (
                <>
                  <Power className="mr-2 h-4 w-4" />
                  Ban Account
                </>
              )
            ) : loadingStates.isUnbanning ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Unbanning...
              </>
            ) : (
              <>
                <Power className="mr-2 h-4 w-4" />
                Unban Account
              </>
            )}
          </Button>
        )}

        {/* Only show Delete Account if user has admin privileges and can manage this user */}
        {hasAdminPrivileges && canManageUserRoles && (
          <Button
            className="w-full text-destructive hover:text-destructive hover:bg-destructive/10"
            variant="outline"
            onClick={onDeleteAccount}
            disabled={isDeleted}
            title={
              isDeleted
                ? "Account is already deleted"
                : "Permanently delete this account"
            }
          >
            <Trash className="mr-2 h-4 w-4" />
            {isDeleted ? "Account Deleted" : "Delete Account"}
          </Button>
        )}

        {/* Permanently Delete All Data (visible as disabled when not deleted) */}
        {hasAdminPrivileges && canManageUserRoles && (
          <Button
            className="w-full text-destructive hover:text-destructive hover:bg-destructive/10"
            variant="outline"
            onClick={onPermanentlyDelete}
            disabled={!isDeleted}
            title={
              isDeleted
                ? "This will purge all user-related data permanently"
                : "Available only for deleted users"
            }
          >
            <Trash className="mr-2 h-4 w-4" />
            Delete All Data Permanently
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

export default UserActions;
